import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, formatApiError, Job, JobDeletionPreview } from '../api/client'
import { fmtDate, fmtDateTime } from '../utils/datetime'
import { useIsAdmin } from '../auth/AuthContext'
import { theme } from '../theme'
import {
  inboundMethodBadge,
  jobInboundLabel,
  jobOutboundLabel,
  outboundMethodBadge,
} from '../utils/methods'
import { legSummaryText } from '../utils/sla'
import { accountRepairSummary, jobNeedsAccountRepair } from '../utils/accountHealth'

/** A run count as a phrase, so the dialog reads as a sentence and never as "1 runs". */
function runsPhrase(n: number): string {
  if (n === 0) return 'no runs yet'
  return n === 1 ? '1 run' : `${n.toLocaleString()} runs`
}

export default function JobList() {
  const isAdmin = useIsAdmin()
  const navigate = useNavigate()
  const [jobs, setJobs] = useState<Job[]>([])
  const [filter, setFilter] = useState<string>('all')
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState<string | null>(null)
  const [cloning, setCloning] = useState<string | null>(null)
  // The outcome banner. A soft delete removes the row from this table and changes nothing
  // else the operator can see, so without this the action looks identical to a hard delete.
  const [notice, setNotice] = useState<string | null>(null)

  // --- Delete confirmation state -------------------------------------------------------
  // The dialog is deliberately NOT window.confirm: it has to state a fetched run count and
  // require the job's name typed, and neither fits in a browser confirm box.
  const [deleteTarget, setDeleteTarget] = useState<Job | null>(null)
  const [preview, setPreview] = useState<JobDeletionPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [typedName, setTypedName] = useState('')
  const [deleting, setDeleting] = useState(false)

  // --- Archived jobs -------------------------------------------------------------------
  const [archived, setArchived] = useState<Job[] | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  // Which archived job is being restored, and the reason a restore was refused. The refusal is
  // held per-list rather than in the top banner because it is about ONE row and names another
  // job — H11's cross-brand mailbox conflict is the refusal an operator actually meets.
  const [restoring, setRestoring] = useState<string | null>(null)
  const [restoreError, setRestoreError] = useState<string | null>(null)

  // A one-field PUT. The backend applies model_dump(exclude_unset=True), so sending only
  // `enabled` leaves every other field — including the stored credentials — untouched.
  // That matters here: this form's history includes a bug where an edit round-trip wiped
  // saved credentials, so a partial update is deliberately used rather than resubmitting
  // the whole job.
  const toggleEnabled = async (job: Job) => {
    if (job.enabled && !window.confirm(
      `Disable "${job.name}"?\n\n` +
      'It will stop sending on its schedule. The definition, credentials and run history ' +
      'are kept, and runs already in flight still complete. You can re-enable it at any time.'
    )) return
    setToggling(job.id)
    try {
      const updated = await api.updateJob(job.id, { enabled: !job.enabled })
      // Patch in place rather than refetching: the list may be filtered to
      // enabled/disabled, and a refetch would make the row the operator just acted on
      // vanish before they saw it change.
      setJobs(prev => prev.map(j => (j.id === job.id ? { ...j, enabled: updated.enabled } : j)))
    } catch (err: any) {
      window.alert(formatApiError(err) || 'Could not change the job state')
    } finally {
      setToggling(null)
    }
  }

  // Clone is a SERVER-side POST, not a prefilled form: the credentials it must copy are never
  // in a response body, so a client-side "read this job then create a new one" would produce a
  // job with no credentials that reported success. The clone is created DISABLED and the editor
  // is opened on it immediately, so every field is editable before anything is ever sent — and
  // nothing is sent, because a disabled job is not scheduled.
  const cloneJob = async (job: Job) => {
    setCloning(job.id)
    try {
      const clone = await api.cloneJob(job.id)
      setNotice(
        `Cloned "${job.name}" as "${clone.name}" — created DISABLED, so it sends nothing yet. ` +
        `Credentials and account references were copied; run history was not. ` +
        (clone.webhook_id
          ? 'A NEW webhook URL was minted for the clone — register that URL with the provider ' +
            'before enabling it. The original job\'s URL is untouched.'
          : 'The clone has no webhook URL, matching its inbound method.')
      )
      navigate(`/jobs/${clone.id}`)
    } catch (err: any) {
      window.alert(formatApiError(err) || 'Could not clone the job')
    } finally {
      setCloning(null)
    }
  }

  // Opening the dialog FETCHES the consequences rather than asserting them. A confirmation that
  // guesses at what is at stake is worse than none: it teaches the operator that the numbers in
  // it are decoration.
  const openDelete = async (job: Job) => {
    setDeleteTarget(job)
    setPreview(null)
    setPreviewError(null)
    setTypedName('')
    try {
      setPreview(await api.deletionPreview(job.id))
    } catch (err: any) {
      setPreviewError(formatApiError(err) || 'Could not read what this delete would affect')
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget || !preview) return
    setDeleting(true)
    try {
      await api.deleteJob(deleteTarget.id)
      setJobs(prev => prev.filter(j => j.id !== deleteTarget.id))
      setNotice(
        `Archived "${deleteTarget.name}". ${runsPhrase(preview.run_count)} kept and still ` +
        `readable in Run History. Scheduling and IMAP monitoring stop within 30 seconds` +
        (preview.runs_in_flight > 0
          ? `, and the ${preview.runs_in_flight} run(s) already in flight will still complete.`
          : '.')
      )
      // The archived list is now stale, and it is the surface that proves the history survived.
      setArchived(null)
      setDeleteTarget(null)
    } catch (err: any) {
      setPreviewError(formatApiError(err) || 'Could not archive the job')
    } finally {
      setDeleting(false)
    }
  }

  const loadArchived = async () => {
    const next = !showArchived
    setShowArchived(next)
    if (next && archived === null) {
      try {
        setArchived(await api.listArchivedJobs())
      } catch {
        setArchived([])
      }
    }
  }

  /**
   * Un-archive a job. The confirmation NAMES the webhook consequence, because that is the
   * whole reason restore exists rather than clone: the job comes back on the URL the provider
   * is already configured with, and nothing needs re-registering. It comes back DISABLED, so
   * the operator's next act is a deliberate one and no fax is sent by this click.
   */
  const restoreJob = async (job: Job) => {
    const webhookLine = job.inbound_method === 'eapi_webhook'
      ? '\n\nIts original webhook URL becomes live again — the same path already registered with ' +
        'the provider, so there is nothing to re-register. Deliveries to it start resolving instead ' +
        'of being dropped.'
      : ''
    if (!window.confirm(
      `Restore "${job.name}"?\n\n` +
      'It returns to the job list DISABLED, with its configuration, credentials and run ' +
      'history intact. Nothing sends until you enable it.' + webhookLine
    )) return
    setRestoring(job.id)
    setRestoreError(null)
    try {
      const restored = await api.restoreJob(job.id)
      setArchived(prev => (prev ?? []).filter(j => j.id !== job.id))
      setJobs(prev => [...prev, restored].sort((a, b) => a.name.localeCompare(b.name)))
      setNotice(
        `Restored "${restored.name}", disabled. Its configuration and run history are unchanged` +
        (restored.webhook_id
          ? ', and its original webhook URL resolves again — nothing to re-register provider-side.'
          : '. Enable it when you are ready for it to send.')
      )
    } catch (err: any) {
      setRestoreError(formatApiError(err) || `Could not restore "${job.name}"`)
    } finally {
      setRestoring(null)
    }
  }

  useEffect(() => {
    const enabled = filter === 'all' ? undefined : filter === 'enabled'
    api.listJobs(enabled).then(setJobs).finally(() => setLoading(false))
  }, [filter])

  // Keep the job listing live: enabled/disabled state and last-run info change
  // underneath the operator as the scheduler works. Refresh WITHOUT touching
  // `loading`, so the table updates in place instead of flashing a spinner.
  useEffect(() => {
    const enabled = filter === 'all' ? undefined : filter === 'enabled'
    const interval = setInterval(() => {
      // Paused while the delete dialog is open: a refresh landing underneath it would swap the
      // row the operator is reading a confirmation about.
      if (deleteTarget) return
      api.listJobs(enabled).then(setJobs).catch(() => { /* transient; retry next tick */ })
    }, 30000)
    return () => clearInterval(interval)
  }, [filter, deleteTarget])

  if (loading) return <div>Loading jobs...</div>

  return (
    <div>
      {/* The outcome, stated. A soft delete makes a row disappear and changes nothing else
          visible, so without this an archive is indistinguishable from a hard delete — and a
          clone is indistinguishable from nothing having happened. */}
      {notice && (
        <div style={{
          marginBottom: '1rem', padding: '0.7rem 0.9rem', background: '#1b2a1b',
          border: '1px solid #2f5d33', borderRadius: '6px', fontSize: '0.85rem',
          lineHeight: 1.5, display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
        }}>
          <span style={{ flex: 1, color: '#c8ddc8' }}>{notice}</span>
          <button
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', color: '#9aa0a6', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h2 style={{ margin: 0 }}>Jobs</h2>
          <Link to="/runs" style={{ color: '#FF4133', textDecoration: 'none', fontSize: '0.85rem', border: '1px solid #5a2a2a', padding: '0.35rem 0.75rem', borderRadius: '4px' }}>
            View Run History
          </Link>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: '0.4rem' }}>
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
          {isAdmin && (
            <Link to="/jobs/new" style={{ background: '#FF4133', color: '#fff', padding: '0.5rem 1rem', borderRadius: '4px', textDecoration: 'none', fontSize: '0.9rem' }}>
              + New Job
            </Link>
          )}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#1e1e1e', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
        <thead>
          <tr style={{ background: '#262626', textAlign: 'left' }}>
            <th style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Name</th>
            <th style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Outbound</th>
            <th style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Inbound</th>
            <th style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Schedule</th>
            <th style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }} title="Per-leg thresholds. A leg shown as 'not evaluated' has none — that is not a pass.">SLA (per leg)</th>
            <th style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Status</th>
            {isAdmin && (
              <th style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {jobs.map(job => (
            <tr key={job.id} style={{ borderTop: '1px solid #333' }}>
              <td style={{ padding: '0.75rem 1rem' }}>
                <Link to={`/jobs/${job.id}`} style={{ color: '#FF4133', textDecoration: 'none', fontWeight: 500 }}>
                  {job.name}
                </Link>
                {/* U28's repair signal. A leg pointing at an account that is gone, disabled, or
                    of the wrong kind is otherwise invisible from here, so finding the jobs that
                    need repointing after an account change meant opening every one of them and
                    reading its dropdown. The badge LINKS TO THE EDITOR because the badge without
                    the route to the fix is the same dead end one step earlier. */}
                {jobNeedsAccountRepair(job) && (
                  <Link
                    to={`/jobs/${job.id}/edit`}
                    title={accountRepairSummary(job)}
                    style={{ display: 'block', marginTop: '0.3rem', fontSize: '0.72rem', color: '#ffa726', textDecoration: 'none' }}
                  >
                    ⚠ account needs repointing — edit
                  </Link>
                )}
              </td>
              <td style={{ padding: '0.75rem 1rem' }}>
                <span style={{ ...outboundMethodBadge(job.outbound_method), padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>
                  {jobOutboundLabel(job.outbound_provider, job.outbound_method)}
                </span>
              </td>
              <td style={{ padding: '0.75rem 1rem' }}>
                <span style={{ ...inboundMethodBadge(job.inbound_method), padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>
                  {jobInboundLabel(job.inbound_provider, job.inbound_method)}
                </span>
              </td>
              <td style={{ padding: '0.75rem 1rem', fontSize: '0.85rem' }}>
                {job.cron_expression || `Every ${job.interval_minutes}min`}
              </td>
              <td style={{ padding: '0.75rem 1rem', fontSize: '0.85rem' }}>{legSummaryText(job)}</td>
              <td style={{ padding: '0.75rem 1rem' }}>
                <span style={{ color: job.enabled ? '#4caf50' : '#9aa0a6', fontSize: '0.85rem' }}>
                  {job.enabled ? '● Active' : '○ Disabled'}
                </span>
                {/* The toggle lives beside the state it changes. Disabling is an
                    operational action — "stop this job now" — and routing it through the
                    full job editor made it look unsupported. Admin-only: it changes
                    whether faxes are sent. */}
                {isAdmin && (
                  <button
                    onClick={() => toggleEnabled(job)}
                    disabled={toggling === job.id}
                    title={job.enabled
                      ? 'Stop dispatching this job. The definition, credentials and history are kept, and runs already in flight still complete.'
                      : 'Resume this job on its existing schedule.'}
                    style={{
                      marginLeft: '0.6rem', padding: '0.15rem 0.45rem', fontSize: '0.7rem',
                      background: theme.bgAlt, color: job.enabled ? '#e0a030' : '#4caf50',
                      border: `1px solid ${job.enabled ? '#6a5320' : '#2f5d33'}`,
                      borderRadius: '4px', cursor: 'pointer',
                    }}
                  >
                    {toggling === job.id ? '…' : job.enabled ? 'Disable' : 'Enable'}
                  </button>
                )}
              </td>
              {isAdmin && (
                <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => cloneJob(job)}
                    disabled={cloning === job.id}
                    title={'Duplicate this job\'s configuration as a new DISABLED job. Credentials and ' +
                      'account references are copied; run history is not, and the clone gets its own ' +
                      'webhook URL so inbound faxes stay attributable.'}
                    style={{
                      padding: '0.2rem 0.5rem', fontSize: '0.72rem', background: theme.bgAlt,
                      color: '#9ab8d8', border: '1px solid #2f4a5d', borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    {cloning === job.id ? '…' : 'Clone'}
                  </button>
                  <button
                    onClick={() => openDelete(job)}
                    title={'Archive this job. Its run history is KEPT — the configuration leaves the ' +
                      'list and stops being scheduled.'}
                    style={{
                      marginLeft: '0.4rem', padding: '0.2rem 0.5rem', fontSize: '0.72rem',
                      background: theme.bgAlt, color: '#e07a6a', border: '1px solid #5a2a2a',
                      borderRadius: '4px', cursor: 'pointer',
                    }}
                  >
                    Delete…
                  </button>
                </td>
              )}
            </tr>
          ))}
          {jobs.length === 0 && (
            <tr><td colSpan={isAdmin ? 7 : 6} style={{ padding: '2rem', textAlign: 'center', color: '#9aa0a6' }}>No jobs configured.{isAdmin ? <> <Link to="/jobs/new">Create one</Link></> : null}</td></tr>
          )}
        </tbody>
      </table>

      {/* The archive, as a surface. A delete here keeps every run row; without somewhere to see
          the archived job those rows are preserved and unreachable, which an operator cannot
          tell apart from deleted. Collapsed by default and fetched on first open, so the normal
          case costs nothing. */}
      <div style={{ marginTop: '1.25rem', fontSize: '0.85rem' }}>
        <button
          onClick={loadArchived}
          style={{
            background: 'none', border: 'none', color: '#9aa0a6', cursor: 'pointer',
            padding: 0, fontSize: '0.85rem', textDecoration: 'underline',
          }}
        >
          {showArchived ? '▾' : '▸'} Archived jobs{archived !== null ? ` (${archived.length})` : ''}
        </button>
        {showArchived && (
          <div style={{ marginTop: '0.6rem', color: '#9aa0a6' }}>
            {archived === null && <div>Loading…</div>}
            {archived !== null && archived.length === 0 && (
              <div>No archived jobs. Deleting a job archives it — nothing is erased.</div>
            )}
            {restoreError && (
              <div
                role="alert"
                style={{
                  margin: '0.5rem 0', padding: '0.5rem 0.65rem', borderRadius: 4,
                  background: '#3a1c1a', border: '1px solid #6b2c26', color: '#f3c8c4',
                }}
              >
                {restoreError}
              </div>
            )}
            {archived !== null && archived.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {archived.map(job => (
                  <li key={job.id} style={{ padding: '0.35rem 0', borderTop: '1px solid #2a2a2a' }}>
                    <span style={{ color: '#c8ccd0' }}>{job.name}</span>
                    {job.deleted_at && (
                      <span style={{ marginLeft: '0.5rem' }}>
                        archived {fmtDateTime(job.deleted_at)}
                      </span>
                    )}
                    <Link to={`/runs?job_id=${job.id}`} style={{ marginLeft: '0.6rem', color: '#FF4133' }}>
                      run history
                    </Link>
                    {/* U14. The archive said "nothing is erased" and offered no way back, so the
                        promise lived only in the delete dialog's copy. Admin-only: restoring is a
                        configuration change, and the API refuses a viewer's POST regardless. */}
                    {isAdmin && (
                      <button
                        onClick={() => restoreJob(job)}
                        disabled={restoring === job.id}
                        title={
                          job.inbound_method === 'eapi_webhook'
                            ? 'Bring this job back on its ORIGINAL webhook URL — the path already ' +
                              'registered with the provider. Returns disabled.'
                            : 'Bring this job back, disabled, with its configuration and history intact.'
                        }
                        style={{
                          marginLeft: '0.6rem', background: 'none', border: '1px solid #3a3a3a',
                          borderRadius: 4, color: restoring === job.id ? '#6b7075' : '#c8ccd0',
                          cursor: restoring === job.id ? 'default' : 'pointer',
                          padding: '0.1rem 0.45rem', fontSize: '0.8rem',
                        }}
                      >
                        {restoring === job.id ? 'Restoring…' : 'Restore'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* The confirmation. It NAMES what is lost and what is kept, with the counts fetched for
          this job — a generic "Are you sure?" would push the decision onto the operator while
          withholding everything needed to make it. Typing the name is the second step: the
          first click is easy to make by accident on a dense table. */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Delete ${deleteTarget.name}`}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem',
          }}
        >
          <div style={{
            background: '#1e1e1e', border: '1px solid #5a2a2a', borderRadius: '8px',
            maxWidth: '560px', width: '100%', padding: '1.25rem', maxHeight: '85vh',
            overflowY: 'auto',
          }}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem' }}>
              Delete “{deleteTarget.name}”?
            </h3>

            {previewError && (
              <div style={{ color: '#e07a6a', marginBottom: '0.75rem' }}>{previewError}</div>
            )}
            {!preview && !previewError && (
              <div style={{ color: '#9aa0a6' }}>Checking what this would affect…</div>
            )}

            {preview && (
              <>
                <p style={{ margin: '0 0 0.6rem', fontSize: '0.88rem', lineHeight: 1.5 }}>
                  This job has <strong>{runsPhrase(preview.run_count)}</strong>
                  {preview.first_run_at && preview.last_run_at && preview.run_count > 0 && (
                    <> recorded between {fmtDate(preview.first_run_at)} and {fmtDate(preview.last_run_at)}</>
                  )}.
                </p>
                <ul style={{ fontSize: '0.85rem', lineHeight: 1.65, margin: '0 0 0.75rem', paddingLeft: '1.1rem' }}>
                  <li>
                    <strong>Run history is kept.</strong> All {runsPhrase(preview.run_count)} stay
                    readable in{' '}
                    <Link to={preview.history_url} style={{ color: '#FF4133' }}>Run History</Link>.
                    Measurements are evidence and are never removed to tidy configuration.
                  </li>
                  <li>The job is <strong>archived</strong>, not erased — it leaves this list and can be restored from <strong>Archived jobs</strong> below, on its original webhook URL.</li>
                  <li>Scheduling and IMAP monitoring <strong>stop within 30 seconds</strong>.</li>
                  {preview.runs_in_flight > 0 && (
                    <li style={{ color: '#e0a030' }}>
                      <strong>{preview.runs_in_flight} run(s) are still in flight.</strong> They are
                      not cancelled — they complete and record their result.
                    </li>
                  )}
                  {preview.webhook_action && (
                    <li>
                      The <strong>webhook URL is kept</strong> on the archived job, so a late
                      provider delivery is still filed against it rather than lost. It stays
                      registered provider-side until you deregister it there.
                      {preview.webhook_url && (
                        <div style={{ color: '#9aa0a6', fontSize: '0.78rem', wordBreak: 'break-all' }}>
                          {preview.webhook_url}
                        </div>
                      )}
                    </li>
                  )}
                  <li>
                    Provider accounts on {preview.leg_accounts.length > 0 ? 'its legs are' : 'this job are'}{' '}
                    <strong>untouched</strong> — they are shared records, and this job keeps
                    protecting them from deletion.
                  </li>
                </ul>

                <label style={{ display: 'block', fontSize: '0.82rem', color: '#9aa0a6', marginBottom: '0.3rem' }}>
                  Type <strong style={{ color: '#c8ccd0' }}>{deleteTarget.name}</strong> to confirm:
                </label>
                <input
                  value={typedName}
                  onChange={e => setTypedName(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%', padding: '0.4rem', background: '#141414', color: '#e8eaed',
                    border: '1px solid #444', borderRadius: '4px', marginBottom: '0.9rem',
                  }}
                />
              </>
            )}

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                style={{
                  padding: '0.4rem 0.9rem', fontSize: '0.85rem', background: theme.bgAlt,
                  color: '#c8ccd0', border: '1px solid #444', borderRadius: '4px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                // Enabled only when the preview loaded AND the name matches exactly. Without the
                // preview the dialog cannot state what is at stake, so it must not be actionable.
                disabled={deleting || !preview || typedName !== deleteTarget.name}
                style={{
                  padding: '0.4rem 0.9rem', fontSize: '0.85rem',
                  background: (preview && typedName === deleteTarget.name) ? '#5a2a2a' : theme.bgAlt,
                  color: (preview && typedName === deleteTarget.name) ? '#ffb0a4' : '#666',
                  border: '1px solid #5a2a2a', borderRadius: '4px',
                  cursor: (preview && typedName === deleteTarget.name) ? 'pointer' : 'not-allowed',
                }}
              >
                {deleting ? 'Archiving…' : 'Archive this job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
